import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import {
  Project,
  ActivityLog as ActivityLogType,
  getProjects,
  getProjectActivity
} from '../../api/client';

const ACTION_TYPE_LABELS: Record<string, string> = {
  'task_created': 'Task Created',
  'task_done': 'Task Completed',
  'task_verified': 'Task Verified',
  'note_added': 'Note Added'
};

const ACTION_TYPE_COLORS: Record<string, string> = {
  'task_created': '#17a2b8',
  'task_done': '#28a745',
  'task_verified': '#6f42c1',
  'note_added': '#fd7e14'
};

export function ActivityLog() {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [activities, setActivities] = useState<ActivityLogType[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      if (!token || !projectId) return;
      try {
        const [projects, activityData] = await Promise.all([
          getProjects(token),
          getProjectActivity(token, parseInt(projectId), actionTypeFilter || undefined)
        ]);

        const found = projects.find(p => p.id === parseInt(projectId));
        if (found) {
          setProject(found);
          setActivities(activityData);
        } else {
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token, isAuthenticated, navigate, actionTypeFilter]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated || loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading...
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate(`/admin/projects/${projectId}`)}
            style={{
              background: 'transparent',
              border: '1px solid #fff',
              color: '#fff',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            Back to Project
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px' }}>
              Activity Log
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
              {project.name}
            </p>
          </div>
        </div>
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Filters */}
        <div style={{
          background: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Filter by Action Type
              </label>
              <select
                value={actionTypeFilter}
                onChange={(e) => setActionTypeFilter(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              >
                <option value="">All Actions</option>
                <option value="task_created">Task Created</option>
                <option value="task_done">Task Completed</option>
                <option value="task_verified">Task Verified</option>
                <option value="note_added">Note Added</option>
              </select>
            </div>
          </div>

          <div style={{
            padding: '10px 16px',
            background: '#fff',
            borderRadius: '4px',
            fontWeight: 'bold',
            display: 'inline-block'
          }}>
            {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'} found
          </div>
        </div>

        {/* Activity Display */}
        {activities.length === 0 ? (
          <div style={{
            background: '#fff',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#666'
          }}>
            <p style={{ margin: 0, fontSize: '16px' }}>
              No activity found{actionTypeFilter ? ' for this action type' : ''}.
            </p>
          </div>
        ) : (
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #ddd'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '180px'
                  }}>
                    When
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '140px'
                  }}>
                    Action
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd'
                  }}>
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {activities.map(activity => (
                  <tr
                    key={activity.id}
                    style={{
                      borderBottom: '1px solid #eee'
                    }}
                  >
                    <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                      {formatDate(activity.created_at)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#fff',
                        background: ACTION_TYPE_COLORS[activity.action_type] || '#6c757d'
                      }}>
                        {ACTION_TYPE_LABELS[activity.action_type] || activity.action_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        maxWidth: '600px',
                        wordBreak: 'break-word'
                      }}>
                        {activity.description}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
